import { useEffect, useRef, useState } from 'react';
import { SHOPS } from '../lib/tenants';

type Props = {
  selected: string[]; // empty = all
  onChange: (next: string[]) => void;
  collapsed?: boolean;
};

export function TenantPicker({ selected, onChange, collapsed }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const filtered = SHOPS.filter(
    (s) =>
      !search ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.code.toLowerCase().includes(search.toLowerCase()),
  );

  const allSelected = selected.length === 0;
  const label =
    selected.length === 0
      ? 'Alle Shops'
      : selected.length === 1
        ? SHOPS.find((s) => s.code === selected[0])?.name ?? 'Shop'
        : `${selected.length} Shops`;
  const meta =
    selected.length === 0
      ? `${SHOPS.length} Länder · alle aggregiert`
      : selected.map((c) => SHOPS.find((s) => s.code === c)?.flag ?? '').join(' ');

  function toggle(code: string) {
    if (selected.includes(code)) onChange(selected.filter((c) => c !== code));
    else onChange([...selected, code]);
  }

  return (
    <div className={`tenant-picker${collapsed ? ' collapsed' : ''}`} ref={ref}>
      <button
        type="button"
        className="tenant-switch"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title={collapsed ? label : undefined}
      >
        <span className="tenant-logo" aria-hidden="true">
          <img src="/tamaris-logo.png" alt="" />
        </span>
        {!collapsed && (
          <>
            <span className="tenant-info">
              <span className="tenant-name">{label}</span>
              <span className="tenant-meta">{meta}</span>
            </span>
            <span className="material-symbols-rounded">unfold_more</span>
          </>
        )}
      </button>
      {open && (
        <div className="tenant-pop">
          <div className="tenant-pop-search">
            <span className="material-symbols-rounded">search</span>
            <input
              autoFocus
              type="text"
              placeholder="Shop suchen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="tenant-pop-list">
            <button type="button" className="tenant-pop-item" onClick={() => onChange([])}>
              <span className={`tenant-checkbox${allSelected ? ' on' : ''}`} aria-hidden="true" />
              <span className="info">
                <span className="tenant-name">Alle Shops</span>
                <span className="meta">Aggregiert über alle Länder</span>
              </span>
              <span className="code">{SHOPS.length}</span>
            </button>
            <div className="tenant-pop-divider" />
            {filtered.map((s) => {
              const on = selected.includes(s.code);
              return (
                <button
                  type="button"
                  key={s.code}
                  className="tenant-pop-item"
                  onClick={() => toggle(s.code)}
                >
                  <span className={`tenant-checkbox${on ? ' on' : ''}`} aria-hidden="true" />
                  <span className="flag" aria-hidden="true">{s.flag}</span>
                  <span className="info">
                    <span className="tenant-name">{s.name}</span>
                    <span className="meta">{s.domain}</span>
                  </span>
                  <span className="code">{s.code}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
